import React, { useContext, useState } from "react";
import { User, Lock, Bell, Save, Loader2 } from "lucide-react";
import { AuthContext } from "../AuthContext";
import API_BASE_URL from "../api";

const API_BASE = `${API_BASE_URL}/api/admin`;

function SettingsCard({ icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-5">
        {icon}
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm text-gray-600">{label}</label>
      {children}
    </div>
  );
}

const inputClass =
  "w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-200";

export default function AdminSettings() {
  const { user, setUser } = useContext(AuthContext);

  const [profile, setProfile] = useState({
    userName: user?.userName || "",
    email: user?.email || "",
  });

  const [password, setPassword] = useState({
    current: "",
    next: "",
    confirm: "",
  });

  const [notifications, setNotifications] = useState({
    newCertificate: true,
    weeklySummary: true,
    systemAlerts: false,
  });

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [message, setMessage] = useState(null);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setMessage(null);
    try {
      // ⚠️ Placeholder endpoint — replace with your real "update admin profile" route.
      const res = await fetch(`${API_BASE}/updateProfile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      }).then((r) => r.json());

      if (res?.user) {
        setUser(res.user);
        localStorage.setItem("user", JSON.stringify(res.user));
      }
      setMessage({ type: "success", text: "Profile updated successfully." });
    } catch (err) {
      console.error("Failed to update profile:", err);
      setMessage({ type: "error", text: "Couldn't update profile. Please try again." });
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (password.next !== password.confirm) {
      setMessage({ type: "error", text: "New passwords do not match." });
      return;
    }
    setSavingPassword(true);
    setMessage(null);
    try {
      // ⚠️ Placeholder endpoint — replace with your real "change password" route.
      await fetch(`${API_BASE}/changePassword`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPassword: password.current,
          newPassword: password.next,
        }),
      }).then((r) => r.json());

      setPassword({ current: "", next: "", confirm: "" });
      setMessage({ type: "success", text: "Password changed successfully." });
    } catch (err) {
      console.error("Failed to change password:", err);
      setMessage({ type: "error", text: "Couldn't change password. Please try again." });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h3 className="text-lg font-bold text-gray-900">Settings</h3>
        <p className="text-sm text-gray-500">Manage your admin profile, security, and notification preferences</p>
      </div>

      {message && (
        <div
          className={`text-xs rounded-lg px-4 py-2 border ${
            message.type === "success"
              ? "text-emerald-700 bg-emerald-50 border-emerald-100"
              : "text-amber-600 bg-amber-50 border-amber-100"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Profile */}
      <SettingsCard icon={<User size={18} className="text-emerald-700" />} title="Profile">
        <form onSubmit={handleProfileSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full Name">
              <input
                type="text"
                value={profile.userName}
                onChange={(e) => setProfile((p) => ({ ...p, userName: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field label="Email Address">
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                className={inputClass}
              />
            </Field>
          </div>
          <button
            type="submit"
            disabled={savingProfile}
            className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white text-sm font-medium rounded-xl px-4 py-2.5 transition-colors"
          >
            {savingProfile ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Changes
          </button>
        </form>
      </SettingsCard>

      {/* Password */}
      <SettingsCard icon={<Lock size={18} className="text-emerald-700" />} title="Change Password">
        <form onSubmit={handlePasswordSave} className="space-y-4">
          <Field label="Current Password">
            <input
              type="password"
              value={password.current}
              onChange={(e) => setPassword((p) => ({ ...p, current: e.target.value }))}
              className={inputClass}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="New Password">
              <input
                type="password"
                value={password.next}
                onChange={(e) => setPassword((p) => ({ ...p, next: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field label="Confirm New Password">
              <input
                type="password"
                value={password.confirm}
                onChange={(e) => setPassword((p) => ({ ...p, confirm: e.target.value }))}
                className={inputClass}
              />
            </Field>
          </div>
          <button
            type="submit"
            disabled={savingPassword}
            className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white text-sm font-medium rounded-xl px-4 py-2.5 transition-colors"
          >
            {savingPassword ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Update Password
          </button>
        </form>
      </SettingsCard>

      {/* Notifications */}
      <SettingsCard icon={<Bell size={18} className="text-emerald-700" />} title="Notification Preferences">
        <div className="space-y-3">
          {[
            { key: "newCertificate", label: "Notify on new certificate generation" },
            { key: "weeklySummary", label: "Weekly summary email" },
            { key: "systemAlerts", label: "System alerts and errors" },
          ].map((item) => (
            <label key={item.key} className="flex items-center justify-between text-sm text-gray-700 cursor-pointer">
              {item.label}
              <input
                type="checkbox"
                checked={notifications[item.key]}
                onChange={(e) =>
                  setNotifications((n) => ({ ...n, [item.key]: e.target.checked }))
                }
                className="w-4 h-4 accent-emerald-700 cursor-pointer"
              />
            </label>
          ))}
        </div>
      </SettingsCard>
    </div>
  );
}